import { useLayoutEffect, useRef } from 'react';

import { Outlet, useLocation, useNavigationType } from 'react-router-dom';

import SeoManager from '@/components/seo/SeoManager';
import HomePopup from '@/features/home/components/HomePopup';
import QuickMenu from '@/features/home/components/QuickMenu';
import SiteFooter from '@/features/home/components/SiteFooter';
import SiteHeader from '@/features/home/components/SiteHeader';
import { useAppLocale } from '@/i18n/useAppLocale';
import { isCatalogCategoryPath, routePaths } from '@/routes/routeRegistry';
import { useHomeUiStore } from '@/stores/useHomeUiStore';
import { classNames } from '@/utils/classNames';

import styles from './RootLayout.module.scss';
import {
  getRestoredScrollTop,
  getDocumentScrollTop,
  isScrollLockKey,
  restoreDocumentScrollTop,
} from './rootLayoutScrollLock';

const RootLayout = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { locale, unprefixedPathname } = useAppLocale();
  const isAboutGreetingRoute = unprefixedPathname === routePaths.aboutGreeting;
  const isAboutHistoryRoute = unprefixedPathname === routePaths.aboutHistory;
  const isAboutCertificationRoute = unprefixedPathname === routePaths.aboutCertification;
  const isAboutOrganizationRoute = unprefixedPathname === routePaths.aboutOrganization;
  const isAboutLocationRoute = unprefixedPathname === routePaths.aboutLocation;
  const isSupportMaterialRoute = unprefixedPathname === routePaths.supportMaterials;
  const isWebCatalogRoute = unprefixedPathname === routePaths.webCatalogs;
  const isPromotionalVideoRoute = unprefixedPathname === routePaths.promotionalVideos;
  const isConstructionCaseRoute = unprefixedPathname === routePaths.constructionCases;
  const isMediaCoverageRoute = unprefixedPathname === routePaths.mediaCoverages;
  const isProjectRecordRoute = unprefixedPathname === routePaths.projectRecords;
  const isWebCatalogPopupRoute =
    unprefixedPathname.startsWith(`${routePaths.webCatalogs}/`) &&
    new URLSearchParams(location.search).get('viewer') === 'popup';
  const isHomeRoute = unprefixedPathname === routePaths.home;
  const isCatalogCategoryRoute = isCatalogCategoryPath(unprefixedPathname);
  const isFullBleedRoute =
    isHomeRoute ||
    isCatalogCategoryRoute ||
    isAboutGreetingRoute ||
    isAboutHistoryRoute ||
    isAboutCertificationRoute ||
    isAboutOrganizationRoute ||
    isAboutLocationRoute ||
    isConstructionCaseRoute ||
    isMediaCoverageRoute ||
    isProjectRecordRoute ||
    isSupportMaterialRoute ||
    isPromotionalVideoRoute ||
    isWebCatalogRoute ||
    isWebCatalogPopupRoute;
  const shouldClipMainX = isHomeRoute;
  const shouldShowQuickMenu = isHomeRoute;
  const introPhase = useHomeUiStore((state) => state.introPhase);
  const resetHomeUi = useHomeUiStore((state) => state.resetHomeUi);
  const heroSectionSelector = isHomeRoute ? '[data-testid="home-hero-section"]' : undefined;
  const scrollPositionsRef = useRef(new Map<string, number>());
  const previousLocationKeyRef = useRef(location.key);

  useLayoutEffect(() => {
    const previousLocationKey = previousLocationKeyRef.current;

    if (previousLocationKey !== location.key) {
      scrollPositionsRef.current.set(previousLocationKey, getDocumentScrollTop());
    }

    const nextScrollTop = getRestoredScrollTop(
      navigationType,
      scrollPositionsRef.current.get(location.key),
    );

    restoreDocumentScrollTop(nextScrollTop);
    previousLocationKeyRef.current = location.key;
  }, [location.key, navigationType]);

  useLayoutEffect(() => {
    document.body.classList.toggle('site-home-route', isHomeRoute);

    if (!isHomeRoute) {
      document.body.classList.remove('home-intro-active');
      document.body.classList.remove('home-intro-complete');
      resetHomeUi();
    }

    return () => {
      document.body.classList.remove('site-home-route');
      document.body.classList.remove('home-intro-active');
      document.body.classList.remove('home-intro-complete');
    };
  }, [isHomeRoute, resetHomeUi]);

  useLayoutEffect(() => {
    if (!isHomeRoute) return;

    const isIntroActive = introPhase === 'active' || introPhase === 'settling';
    const isIntroComplete = introPhase === 'complete';

    document.body.classList.toggle('home-intro-active', isIntroActive);
    document.body.classList.toggle('home-intro-complete', isIntroComplete);
  }, [introPhase, isHomeRoute]);

  useLayoutEffect(() => {
    if (!isHomeRoute || (introPhase !== 'active' && introPhase !== 'settling')) {
      return;
    }

    const lockedScrollTop = getDocumentScrollTop();

    const keepScrollPosition = () => {
      if (getDocumentScrollTop() === lockedScrollTop) {
        return;
      }

      restoreDocumentScrollTop(lockedScrollTop);
    };

    const preventScrollInput = (event: Event) => {
      event.preventDefault();
      keepScrollPosition();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isScrollLockKey(event.key)) {
        return;
      }

      preventScrollInput(event);
    };

    keepScrollPosition();

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', keepScrollPosition, { passive: true });
    window.addEventListener('touchmove', preventScrollInput, { passive: false });
    window.addEventListener('wheel', preventScrollInput, { passive: false });

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', keepScrollPosition);
      window.removeEventListener('touchmove', preventScrollInput);
      window.removeEventListener('wheel', preventScrollInput);
    };
  }, [introPhase, isHomeRoute]);

  return (
    <div className={styles['shell']}>
      <SeoManager />
      {isHomeRoute ? <HomePopup /> : null}
      {isWebCatalogPopupRoute ? null : (
        <SiteHeader
          heroSectionSelector={heroSectionSelector}
          isHomeRoute={isHomeRoute}
          key={`${locale}:${location.pathname}${location.search}`}
        />
      )}

      <main
        className={classNames(
          styles['main'],
          isFullBleedRoute && styles['mainFullBleed'],
          isWebCatalogPopupRoute && styles['mainPopup'],
          shouldClipMainX && styles['mainClipX'],
          isHomeRoute && styles['mainHome'],
        )}
      >
        <Outlet />
      </main>

      {isWebCatalogPopupRoute ? null : <SiteFooter />}
      {shouldShowQuickMenu && !isWebCatalogPopupRoute ? <QuickMenu /> : null}
    </div>
  );
};

export default RootLayout;
